import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import { CloudUpload as UploadIcon, CheckCircle as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import { importApi, ImportJob, FieldMapping, ImportConfig } from '../../services/dataOnboardingApi';

const steps = ['Upload File', 'Map Columns', 'Process Import', 'Results'];

// Available data types for import
const dataTypes = [
  { value: 'products', label: 'Products' },
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'components', label: 'Components' },
  { value: 'warehouses', label: 'Warehouses' },
  { value: 'locations', label: 'Locations' },
];

export const DataImportPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [dataType, setDataType] = useState<string>('products');
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [suggestedMapping, setSuggestedMapping] = useState<Record<string, string>>({});
  const [sourceColumns, setSourceColumns] = useState<string[]>([]);
  const [targetColumns, setTargetColumns] = useState<string[]>([]);
  const [importStatus, setImportStatus] = useState<ImportJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Upload File
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      // Upload file with data type
      const uploadResponse = await importApi.uploadFile(file, dataType);
      setImportJob(uploadResponse);
      
      // Preview the import to get columns and mapping suggestions
      const previewResponse = await importApi.previewImport(uploadResponse.id);
      setSourceColumns(previewResponse.columns);
      setTargetColumns(previewResponse.columns); // In real app, get target schema columns
      setSuggestedMapping(previewResponse.suggested_mappings);
      setMapping(previewResponse.suggested_mappings); // Default to suggestions
      
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Map Columns
  const handleMappingChange = (sourceCol: string, targetCol: string) => {
    setMapping(prev => ({
      ...prev,
      [sourceCol]: targetCol
    }));
  };

  const handleConfirmMapping = async () => {
    if (!importJob) return;
    setActiveStep(2);
  };

  // Step 3: Process Import
  const handleProcessImport = async () => {
    if (!importJob) return;
    setLoading(true);
    setError(null);
    try {
      // Build field mappings from user selections
      const fieldMappings: FieldMapping[] = Object.entries(mapping)
        .filter(([_, target]) => target !== '')
        .map(([source, target]) => ({
          source_field: source,
          target_field: target
        }));

      const config: ImportConfig = {
        data_type: dataType as ImportConfig['data_type'],
        field_mappings: fieldMappings,
        skip_header: true,
        date_format: 'DD/MM/YYYY',
        currency: 'GBP'
      };

      const response = await importApi.executeImport(importJob.id, config);
      setImportStatus({
        ...importJob,
        status: response.status as ImportJob['status'],
        result: response.result
      });
      setActiveStep(3);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to start import process');
    } finally {
      setLoading(false);
    }
  };

  // Polling for status (when processing takes longer)
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (activeStep === 2 && importJob && importStatus?.status === 'processing') {
      const checkStatus = async () => {
        try {
          const status = await importApi.getStatus(importJob.id);
          setImportStatus(status);

          if (status.status === 'completed' || status.status === 'failed') {
            setActiveStep(3);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Polling error", err);
        }
      };

      intervalId = setInterval(checkStatus, 2000); // Poll every 2 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeStep, importJob, importStatus?.status]);


  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2, textAlign: 'center', p: 4, border: '2px dashed #ccc', borderRadius: 2 }}>
            {/* Data Type Selector */}
            <FormControl sx={{ minWidth: 200, mb: 3 }}>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={dataType}
                label="Data Type"
                onChange={(e) => setDataType(e.target.value)}
              >
                {dataTypes.map((dt) => (
                  <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box>
              <input
                accept=".csv,.xlsx,.json"
                style={{ display: 'none' }}
                id="raised-button-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                  Select File
                </Button>
              </label>
            </Box>
            {file && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                Selected: {file.name}
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleUpload} 
                disabled={!file || loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Upload & Analyse'}
              </Button>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Map Columns</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Match columns from your file to the system's data structure.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {sourceColumns.map((sourceCol) => (
                <Grid item xs={12} md={6} key={sourceCol}>
                  <Card variant="outlined">
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="subtitle2">{sourceCol}</Typography>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Target Field</InputLabel>
                        <Select
                          value={mapping[sourceCol] || ''}
                          label="Target Field"
                          onChange={(e) => handleMappingChange(sourceCol, e.target.value)}
                        >
                          <MenuItem value=""><em>Ignore</em></MenuItem>
                          {targetColumns.map((targetCol) => (
                            <MenuItem key={targetCol} value={targetCol}>{targetCol}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>Back</Button>
              <Button variant="contained" onClick={handleConfirmMapping} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Confirm Mapping'}
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {importStatus?.status !== 'processing' ? (
               <Box>
                 <Typography variant="h6" gutterBottom>Ready to Import</Typography>
                 <Typography paragraph>
                   File uploaded and mapping confirmed. Click below to start processing.
                 </Typography>
                 <Button variant="contained" color="primary" onClick={handleProcessImport} disabled={loading}>
                   {loading ? <CircularProgress size={24} /> : 'Start Import Process'}
                 </Button>
               </Box>
            ) : (
              <Box>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Processing Import...
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Status: {importStatus?.status || 'Initialising'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            {importStatus?.status === 'completed' ? (
              <Alert severity="success" icon={<CheckIcon fontSize="inherit" />}>
                <Typography variant="h6">Import Completed Successfully</Typography>
                <Typography variant="body2">
                  {importStatus.result?.imported || 0} records imported.
                  {importStatus.result?.skipped ? ` ${importStatus.result.skipped} skipped.` : ''}
                  {importStatus.result?.errors ? ` ${importStatus.result.errors} errors.` : ''}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon fontSize="inherit" />}>
                <Typography variant="h6">Import Failed</Typography>
                <Typography variant="body2">
                  {importStatus?.validation?.errors?.[0]?.message || 'Unknown error occurred.'}
                </Typography>
              </Alert>
            )}
            
            {importStatus?.validation?.errors && importStatus.validation.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Details:</Typography>
                <List dense>
                  {importStatus.validation.errors.slice(0, 10).map((err, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={`Row ${err.row}: ${err.message}`}
                        secondary={`Field: ${err.field}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => {
                setActiveStep(0);
                setFile(null);
                setImportJob(null);
                setImportStatus(null);
                setMapping({});
                setSourceColumns([]);
              }}>
                Import Another File
              </Button>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Import
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Upload and map your data to the ASCIN platform.
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {renderStepContent(activeStep)}
        </Box>
      </Paper>
    </Box>
  );
};

export default DataImportPage;
